import { WritableDraft } from '@reduxjs/toolkit';
import { Campaign } from './campaign';
import { Employee } from './employee';
import { Note } from './note';
import { Respondent } from './respondent';
import { Response } from './response';
import type { CampaignMemberActivity } from '@nps.today/component-library';

export enum CampaignMemberCategory {
  RESPONSES = 'responses',
  DELIVERED = 'delivered',
  SCHEDULED = 'scheduled',
  UNSUBSCRIBED = 'unsubscribed',
  FAILED = 'failed',
  EXPIRED = 'expired'
}

export const campaignMemberCategoryAditionStatusMap = new Map([
  [CampaignMemberCategory.RESPONSES, { min: 2500, max: 2999, exclude: [] }],
  [CampaignMemberCategory.DELIVERED, { min: 2000, max: 2499, exclude: [] }],
  [CampaignMemberCategory.SCHEDULED, { max: 999, exclude: [] }],
  [CampaignMemberCategory.UNSUBSCRIBED, { min: 3000, max: 3999, exclude: [] }],
  [CampaignMemberCategory.FAILED, { min: 4000, max: 5999, exclude: [4010] }],
  [CampaignMemberCategory.EXPIRED, { min: 4010, max: 4010, exclude: [] }]
]);

export type CampaignMembersData = {
  total: number;
  skip: number;
  take: number;
  campaignMembers: CampaignMember[];
};

export type {
  CampaignMemberActivity,
  CampaignMemberActivityActorType
} from '@nps.today/component-library';

export type CampaignMemberState = {
  isLoading: {
    [key: string]: boolean;
  };
  campaignMember: CampaignMember | undefined;
  notes: Note[];
  activities: CampaignMemberActivity[];
  campaignMembersData: WritableDraft<{
    [key in CampaignMemberCategory]: CampaignMembersData;
  }>;
  updateCampaignMembers: number;
};

export type CampaignMember = {
  id: number;
  publicGuid: string;
  note: null;
  noteBy: null;
  respondentId: number;
  campaignId: number;
  responsibleEmployeeId: number;
  doNotContact: boolean;
  sender: number;
  firstName: string;
  lastName: null | string;
  title: null;
  department: null;
  address1: null;
  address2: null;
  city: null;
  country: null;
  zipCode: null;
  phoneNumber: null | string;
  externalId: null;
  emailAddress: string;
  companyId: null;
  companyName: null;
  companyIndustry: null;
  companyAccountNo: null;
  companySegment: null;
  companyEmailAddress: null;
  companyPhoneNumber: null;
  companyAddress1: null;
  companyAddress2: null;
  companyCity: null;
  companyCountry: null;
  companyZipCode: null;
  created: Date;
  custom: null | string;
  agentRating: number | null;
  agentAnswers: null;
  additionStatus: number;
  firstSurveyExposure: Date;
  orgId: string;
  lastChangeBy: string;
  anonymous: boolean;
  response?: Response;
  responsibleEmployee?: Employee;
  respondent?: Respondent;
  campaign: Campaign;
  emails: Email[];
  sms: Sms[];

  /* Not part of original object */
  surveySendTime?: Date;
  surveyScheduledTime?: Date;
  reminderSendTime?: Date;
  reminderScheduledTime?: Date;
};

export type Email = {
  emailStatus: string;
  emailType: string;
  scheduledTime?: Date;
  sendTime?: Date;
};

export type Sms = {
  smsStatus: string;
  smsType: string;
  scheduledTime?: Date;
  sendTime?: Date;
};

export enum CampaignMemberAdditionStatus {
  // 1xxx = success status pre-survey
  Added = 0,
  Forwarded = 1,
  Scheduled = 10,

  // 2xxx = success status post-survey
  Sent = 2010,
  Delivered = 2020,
  Opened = 2030,
  Clicked = 2040,
  Reminded = 2050,
  ReminderWasUnsuccessful = 2051,

  // 21xx = success status post-response
  Rated = 2510,
  Commented = 2520,
  Completed = 2600,

  ScoreChangedInternally = 2701,
  CommentChangedInternally = 2702,

  // 3xxx = unexpected status post-response
  Unsubscribed = 3010,

  // 4xxx = survey cancelled reason pre email/sms service
  InternalUnknownError = 4000,
  Duplicate = 4001,
  Throttled = 4002,
  CallDurationTooShort = 4003,
  InvalidPhone = 4004,
  InvalidEmail = 4005,
  AlreadyUnsubscribed = 4006,
  CampaignEnded = 4007,
  DuplicateDetected = 4008,
  WasAnonymousSendout = 4009,
  SurveyExpired = 4010,
  SMSCountryBlocked = 4011,
  ReminderCancelled = 4012,
  UsageLimitReached = 4013,

  // 5xxx = survey fail reason post email/sms service
  ExternalUnknownError = 5000,
  ExternalBounced = 5001,

  // 51xx survey fail reason post email service
  EmailServiceFailed = 5100,
  EmailServiceBounced = 5101,
  EmailReminderBounced = 5102,

  // 52xx survey fail reason post sms service
  SMSServiceFailed = 5200,
  SMSServiceBounced = 5201,
  SMSServiceNumberNotCapableOfReceivingSMS = 5202
}
